import React, { ReactElement, useEffect, useState } from 'react';
import { Alert, Button, Col, Layout, Row, Typography } from 'antd';
import Loading from '../loading';
import { useLocation } from 'react-router-dom';
import NotificationAPIClient from 'notificationapi-js-client-sdk';
import {
  InitOptions,
  WS_UserPreferencesPatchRequest
} from 'notificationapi-js-client-sdk/lib/interfaces';

const { Content } = Layout;
const { Text, Title } = Typography;

const Unsubscribe = (): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [badParams, setBadParams] = useState(false);
  const [notificationapi, setNotificationapi] =
    useState<NotificationAPIClient>();
  const [resubscribed, setResubscribed] = useState(false);
  const [logo, setLogo] = useState('');

  function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  const query = useQuery();

  const resubscribe = () => {
    const channelId = query.get('channelId');
    const notificationId = query.get('notificationId');

    if (notificationId && channelId && notificationapi) {
      const message: WS_UserPreferencesPatchRequest = {
        route: 'user_preferences/patch_preferences',
        payload: [
          {
            notificationId,
            channelPreferences: [
              {
                channel: channelId,
                state: true
              }
            ]
          }
        ]
      };
      notificationapi.sendWSMessage(message);
      setResubscribed(true);
    }
  };

  useEffect(() => {
    document.title = 'Unsubscribe';
    const envId = query.get('envId');
    const userId = query.get('userId');
    const channelId = query.get('channelId');
    const notificationId = query.get('notificationId');
    const hashedUserId = query.get('hashedUserId');

    if (envId && userId && channelId && notificationId) {
      const initParams: InitOptions = {
        clientId: envId,
        userId: userId
      };
      if (hashedUserId) {
        initParams['userIdHash'] = hashedUserId;
      }
      const notificationapi = new NotificationAPIClient(initParams);
      setNotificationapi(notificationapi);

      const sendWSRequests = () => {
        const message: WS_UserPreferencesPatchRequest = {
          route: 'user_preferences/patch_preferences',
          payload: [
            {
              notificationId,
              channelPreferences: [
                {
                  channel: channelId,
                  state: false
                }
              ]
            }
          ]
        };
        notificationapi.sendWSMessage(message);

        notificationapi.websocket?.addEventListener('message', (message) => {
          const body = JSON.parse(message.data);
          if (body && body.route === 'environment/data' && body.payload.logo) {
            setLogo(body.payload.logo);
            setLoading(false);
          }
        });
        notificationapi.websocket?.send(
          JSON.stringify({
            route: 'environment/data'
          })
        );
      };
      if (
        notificationapi.websocket?.readyState ===
        notificationapi.websocket?.OPEN
      ) {
        sendWSRequests();
      } else {
        notificationapi.websocket?.addEventListener('open', () => {
          sendWSRequests();
        });
      }
    } else {
      setLoading(false);
      setBadParams(true);
    }
  }, [query]);

  return (
    <Layout>
      <Content
        style={{
          background: '#fff',
          minHeight: '100vh',
          textAlign: 'center'
        }}
      >
        {loading && (
          <div style={{ marginTop: '35vh' }}>
            <Loading />
          </div>
        )}
        {!loading && (
          <>
            {badParams && (
              <Row style={{ marginTop: '35vh' }} justify="center">
                <Col>
                  <Alert
                    showIcon
                    message="The unsubscribe link seems incorrect."
                    type="error"
                    style={{ maxWidth: '500px' }}
                  />
                </Col>
              </Row>
            )}
            {!badParams && (
              <>
                <Row style={{ marginTop: '20vh' }}>
                  <Col span={22} offset={1}>
                    <img
                      src={logo ?? './img/unsubscribe.png'}
                      alt="logo"
                      style={{ width: '277px' }}
                      onError={(e) => {
                        setLogo('./img/unsubscribe.png');
                      }}
                    />
                  </Col>
                </Row>
                {!resubscribed && (
                  <>
                    <Row style={{ marginTop: '60px' }}>
                      <Col span={22} offset={1}>
                        <Title level={2}>Unsubscribe Successful</Title>
                      </Col>
                    </Row>
                    <Row style={{ marginTop: '20px' }}>
                      <Col span={22} offset={1}>
                        <Text>
                          You will no longer receive this type of notification.
                        </Text>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={22} offset={1}>
                        <Text>
                          If this was done in error, re-subscribe using the
                          button below.
                        </Text>
                      </Col>
                    </Row>
                    <Row style={{ marginTop: '20px', marginBottom: '10vh' }}>
                      <Col span={22} offset={1}>
                        <Button
                          style={{ minWidth: '200px' }}
                          type="primary"
                          onClick={resubscribe}
                        >
                          Resubscribe
                        </Button>
                      </Col>
                    </Row>
                  </>
                )}
                {resubscribed && (
                  <Row style={{ marginTop: '60px' }}>
                    <Col span={22} offset={1}>
                      <Title level={2}>Subscribed Back!</Title>
                    </Col>
                  </Row>
                )}
              </>
            )}
          </>
        )}
      </Content>
    </Layout>
  );
};

export default Unsubscribe;
